import React, { type ReactNode, useMemo, useRef } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { PostOfficeUserInteractionHandlers } from '@atlassian/post-office-analytics/types';
import type { SerializableRecord } from '@atlassian/post-office-placement-data/types';

import type { PostOfficeMessageProps } from '../components/PostOfficeMessage';
import { PostOfficeMessage } from '../components/PostOfficeMessage';
import { transformPlacementMessageToMessageLifecycleProps } from '../util/transformPlacementMessageToMessageLifecycleProps';
import type {
	UseMessageInstancesMatchHelpers,
	UseMessageInstancesOptions,
} from './useMessageInstances';
import { useMessageInstances } from './useMessageInstances';
import type { PlacementMessage, PlacementMessageDiscriminant } from './usePlacementData';
import { MultipleMessageInstancesError } from './utils';

/**
 * A `PostOfficeMessage` wrapper pre-bound to the resolved message instance.
 * `messageInstanceId`, `messageTemplateId`, and `placementId` are already closed over —
 * `messageCategory` is closed over from the fetched message.
 * Only `children` needs to be supplied in the common case.
 * This is `undefined` when no message is available.
 *
 * @see https://developer.atlassian.com/platform/post-office/analytics-and-reporting/instrumenting-user-interactions/
 */
export type PostOfficeMessageData<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = (
	props: Omit<
		PostOfficeMessageProps,
		'messageInstanceId' | 'messageTemplateId' | 'placementId' | 'messageCategory' | 'children'
	> & {
		/**
		 * Receives the resolved message, plus the Post Office user-interaction handlers bound to
		 * it — so a headless consumer records what the user decided without wiring up analytics:
		 *
		 * ```tsx
		 * {(message, interactions) => {
		 *   const { onCtaActioned } = interactions!;
		 *   return <MyUI onTry={() => onCtaActioned({ interactionIdentifier: 'tryIt' })} />;
		 * }}
		 * ```
		 *
		 * `interactions` is always supplied at runtime. The `?` is temporary and will be removed
		 * once existing call sites are migrated — assert once when destructuring rather than
		 * optional-chaining every call.
		 */
		children: (
			message: PlacementMessage<TMessage>,
			interactions?: PostOfficeUserInteractionHandlers,
		) => ReactNode;
	},
) => JSX.Element | null;

export type UseMessageDataResult<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = {
	/**
	 * The single non-dismissed message instance for the specified template, or `undefined`.
	 * `undefined` if no message exists, the message is dismissed, or multiple instances exist
	 * (in which case `error` is set to `MultipleMessageInstancesError`).
	 * Use `useMessageInstances` if multiple instances are expected.
	 *
	 * Only present when `dataOutsideWrapper` is explicitly `true`. When `false` (the default),
	 * {@link UseMessageDataResultWithoutData} is returned.
	 */
	message: PlacementMessage<TMessage> | undefined;
	/**
	 * The `messageInstanceId` of the resolved message instance, or `undefined` when no single
	 * message is available.
	 */
	messageInstanceId?: string;
	/**
	 * A `PostOfficeMessage` pre-bound with `messageInstanceId`, `messageTemplateId`, and `placementId`.
	 * `undefined` when no message is available.
	 * `messageCategory` is closed over from the fetched message.
	 * Only `children` needs to be passed in the common case.
	 *
	 * @example
	 * ```tsx
	 * const { PostOfficeMessage } = useMessageData({ messageTemplateId });
	 * if (!PostOfficeMessage) return null;
	 * return (
	 *   <PostOfficeMessage>
	 *     {(message) => <MyUI content={message.context} />}
	 *   </PostOfficeMessage>
	 * );
	 * ```
	 */
	PostOfficeMessage: PostOfficeMessageData<TMessage> | undefined;
	dismissedIds: ReadonlySet<string>;
	/** Whether the initial Placement API fetch is in progress. */
	isLoading: boolean;
	/** Any error returned by the Placement API fetch or validation. */
	error: Error | undefined;
	dismissMessage: (messageInstanceId: string) => void;
};

/**
 * Return type of `useMessageData` when `dataOutsideWrapper` is `false`.
 * Identical to {@link UseMessageDataResult} but without `message`. Message data can
 * be accessed inside the `PostOfficeMessage` render prop.
 */
export type UseMessageDataResultWithoutData<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
> = Omit<UseMessageDataResult<TMessage>, 'message'>;

export type UseMessageDataMatchHelpers<
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
> = UseMessageInstancesMatchHelpers<TMessage, TExtension>;

export type UseMessageDataOptions<
	TMessage extends PlacementMessageDiscriminant,
	TExtension extends SerializableRecord,
> = UseMessageInstancesOptions<TMessage, TExtension>;

export type UseMessageDataSelector = {
	/** The Post Office placement ID (e.g. `'some-placement'`). Defaults to `'headless'`. */
	placementId?: string;
	/** The message template ID to filter by (e.g. `'some-template'`). */
	messageTemplateId: string;
};

/**
 * Returns the single non-dismissed message instance for a specific template.
 * If multiple instances exist, `message` and `PostOfficeMessage` are `undefined`
 * and `error` is set to `MultipleMessageInstancesError`.
 *
 * By default, render the message through the returned `PostOfficeMessage` wrapper and read the
 * message data from its render prop. This is the recommended usage — the wrapper keeps
 * `viewed`/`delivered` analytics and choreographer gating wired up, and you get the message data
 * without opting into `dataOutsideWrapper`:
 *
 * ```tsx
 * const { PostOfficeMessage } = useMessageData({ messageTemplateId: 'some-template' });
 * if (!PostOfficeMessage) return null;
 * return (
 *   <PostOfficeMessage>
 *     {(message) => <MyUI content={message.context} />}
 *   </PostOfficeMessage>
 * );
 * ```
 *
 * Provide `options.match` when the message lives under a non-standard key or you need to filter
 * the candidate messages before the `messageTemplateId` match is applied:
 *
 * ```tsx
 * const { PostOfficeMessage } = useMessageData(
 *   { messageTemplateId: 'some-template' },
 *   { match: ({ matchBuilder }) => matchBuilder.messageLocation('triggeredMessages') },
 * );
 * ```
 * Use `useMessageInstances` to receive all instances of a template.
 */
export function useMessageData<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	selector: UseMessageDataSelector,
	options?: UseMessageDataOptions<TMessage, TExtension> & {
		dataOutsideWrapper?: false | undefined;
	},
): UseMessageDataResultWithoutData<TMessage>;
export function useMessageData<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	selector: UseMessageDataSelector,
	options: UseMessageDataOptions<TMessage, TExtension> & { dataOutsideWrapper: true },
): UseMessageDataResult<TMessage>;
export function useMessageData<
	TMessage extends PlacementMessageDiscriminant = PlacementMessageDiscriminant,
	TExtension extends SerializableRecord = Record<never, never>,
>(
	selector: UseMessageDataSelector,
	options?: UseMessageDataOptions<TMessage, TExtension> & { dataOutsideWrapper?: boolean },
): UseMessageDataResult<TMessage> | UseMessageDataResultWithoutData<TMessage> {
	const { placementId = 'headless', messageTemplateId } = selector;

	const dataOutsideWrapper = options?.dataOutsideWrapper ?? false;
	const hisiMigrationGovernance = options?.hisiMigrationGovernance;
	const isDisabledByHISIMigrationKillSwitch =
		hisiMigrationGovernance &&
		hisiMigrationGovernance.bothModeFgEnabled === false &&
		hisiMigrationGovernance.poOnlyFgEnabled === false &&
		fg('post-office_enable_hisi-migration-killswitch');
	/*
	 * To ensure HISI <> PO parity, identify which template made the request to Post Office.
	 * Without this, all Headless API requests otherwise look the same.
	 */
	const maybeEnrichedQueryString =
		hisiMigrationGovernance && fg('post-office_enable_hisi-migration-query-param')
			? {
					...options?.queryString,
					hisiMigrationTemplateId: messageTemplateId,
				}
			: options?.queryString;

	// `useMessageData` always needs the resolved messages internally to enforce the
	// single-instance rule and to pre-bind the wrapper, so opt into external data on the
	// internal call. Whether the message is exposed to the caller is decided below.
	//
	// An empty placement ID gives SWR a null key, preventing the request.
	const { messages, dismissedIds, isLoading, error, dismissMessage, traceId } = useMessageInstances<
		TMessage,
		TExtension
	>(isDisabledByHISIMigrationKillSwitch ? { ...selector, placementId: '' } : selector, {
		...options,
		queryString: maybeEnrichedQueryString,
		dataOutsideWrapper: true,
	});
	const traceIdRef = useRef(traceId);
	traceIdRef.current = traceId;

	const multipleInstancesError = useMemo(
		() =>
			messages.length > 1
				? new MultipleMessageInstancesError({
						placementId,
						messageTemplateId,
						instanceCount: messages.length,
					})
				: undefined,
		[messages.length, messageTemplateId, placementId],
	);

	const firstMessage = multipleInstancesError ? undefined : messages[0];

	const firstMessageRef = useRef<PlacementMessage<TMessage> | undefined>(firstMessage);
	firstMessageRef.current = firstMessage;

	// Extract stable scalar primitives from `message` before the memo so the dep array only
	// contains primitives — avoiding a new object reference on every render. `message` itself
	// is a new object on each render (spread from the messages array), so using it directly
	// as a dep would recreate `MessageComponent` every render and cause unnecessary
	// re-mounts of the consumer's subtree.
	const messageInstanceId = firstMessage?.messageInstanceId;
	const messageTemplateIdFromMessage = firstMessage?.messageTemplateId;
	const messageCategoryFromMessage = firstMessage?.messageCategory;
	const triggerId = firstMessage?.triggerId;
	const messageCreationType = firstMessage?.messageCreationType;
	const journeyId = firstMessage?.journeyId;
	const journeyTriggerId = firstMessage?.journeyTriggerId;
	const configSetId = firstMessage?.configSetId;
	const lifecycleProps = useMemo(
		() =>
			transformPlacementMessageToMessageLifecycleProps({
				message: {
					configSetId,
					journeyId,
					journeyTriggerId,
					messageCreationType,
					triggerId,
				},
			}),
		[configSetId, journeyId, journeyTriggerId, messageCreationType, triggerId],
	);

	const PostOfficeMessageDataWrapper = useMemo<PostOfficeMessageData<TMessage> | undefined>(() => {
		if (!messageInstanceId || !messageTemplateIdFromMessage) {
			return undefined;
		}

		return ({ children, ...props }) => (
			<PostOfficeMessage
				messageInstanceId={messageInstanceId}
				messageTemplateId={messageTemplateIdFromMessage}
				placementId={placementId}
				messageCategory={messageCategoryFromMessage}
				{...lifecycleProps}
				inAppNarrowExposureExperiment={firstMessageRef.current?.inAppNarrowExposureExperiment}
				{...props}
				traceId={traceIdRef.current}
			>
				{(interactions) => children(firstMessageRef.current!, interactions)}
			</PostOfficeMessage>
		);
	}, [
		lifecycleProps,
		messageCategoryFromMessage,
		messageInstanceId,
		messageTemplateIdFromMessage,
		placementId,
	]);

	return dataOutsideWrapper
		? {
				message: firstMessage,
				messageInstanceId,
				PostOfficeMessage: PostOfficeMessageDataWrapper,
				dismissedIds,
				isLoading,
				error: error ?? multipleInstancesError,
				dismissMessage,
			}
		: {
				messageInstanceId,
				PostOfficeMessage: PostOfficeMessageDataWrapper,
				dismissedIds,
				isLoading,
				error: error ?? multipleInstancesError,
				dismissMessage,
			};
}
